import { useTranslation } from 'react-i18next'
import { queryClient } from '../api/queryClient'
import { queryKeys } from '../api/queryKeys'
import type {
  InvoiceAiAnalysisCompleted,
  InvoiceAiAnalysisErrorEvent,
  InvoiceUploadedErrorEvent,
  InvoiceUploadedEvent,
} from '../api/types/generated-types'
import { useUploadingInvoiceStore } from '../features/energiekosten/invoice-audit/store/uploading-invoice-store'
import { useBaseToast } from './useBaseToast'
import { useSocketIoListener } from './useSocketIoListener.hook'

export const useInvoiceUploadListener = () => {
  const { removeInvoiceId } = useUploadingInvoiceStore()
  const toast = useBaseToast()
  const { t } = useTranslation()

  const invalidateInvoiceAuditsQueries = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.procurement.invoiceAudits.manyByCompanyId.toKey(),
    })
    queryClient.invalidateQueries({
      queryKey: queryKeys.procurement.invoiceAudits.manyByLocationId.toKey(),
    })
    queryClient.invalidateQueries({
      queryKey: queryKeys.procurement.procurementInvoices.manyByCompanyId.toKey(),
    })
    queryClient.invalidateQueries({
      queryKey: queryKeys.procurement.procurementInvoices.manyByLocationId.toKey(),
    })
  }

  const handleInvoiceUploaded = (event: InvoiceUploadedEvent) => {
    invalidateInvoiceAuditsQueries()
  }

  const handleInvoiceAiAnalysisCompleted = (event: InvoiceAiAnalysisCompleted) => {
    removeInvoiceId(event.invoiceId)
    invalidateInvoiceAuditsQueries()
  }

  const handleInvoiceUploadError = (event: InvoiceUploadedErrorEvent) => {
    removeInvoiceId(event.invoiceId)
    toast({
      id: 'invoice-upload-error',
      description: t('invoice_upload_failed'),
      status: 'error',
    })
  }

  const handleInvoiceAiAnalysisError = (event: InvoiceAiAnalysisErrorEvent) => {
    removeInvoiceId(event.invoiceId)
    toast({
      id: 'invoice-ai-analysis-error',
      description: t('invoice_analysis_failed'),
      status: 'error',
    })
  }

  useSocketIoListener('invoiceUpload.invoiceUploaded', handleInvoiceUploaded)
  useSocketIoListener('invoiceUpload.invoiceAiAnalysisCompleted', handleInvoiceAiAnalysisCompleted)
  useSocketIoListener('invoiceUpload.invoiceUploadError', handleInvoiceUploadError)
  useSocketIoListener('invoiceUpload.invoiceAiAnalysisError', handleInvoiceAiAnalysisError)
}
